import { useEffect, type ComponentType, type ReactNode } from 'react';
import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useAuthStore } from '../store/useAuthStore';
import { useSession } from '../lib/auth/useSession';
import type { Capability } from '../lib/auth/capabilities';
import { setNoNetworkNavigator, setServiceUnavailableNavigator } from '../lib/api/client';
import { NAV_ITEMS, homePathFor, type NavItem } from './shell/nav';
import { AdminShell } from './shell/AdminShell';
import { SignInPage } from './pages/SignInPage';
import { PlaceholderScreen } from './pages/PlaceholderScreen';
import { ChefsScreen } from '../features/chefs/ChefsScreen';
import { EatersScreen } from '../features/eaters/EatersScreen';
import { DashboardScreen } from '../features/dashboard/DashboardScreen';
import { CommunityActivationScreen } from '../features/analytics/CommunityActivationScreen';
import { GrowthScreen } from '../features/analytics/GrowthScreen';
import { FeedbackScreen } from '../features/feedback/FeedbackScreen';
import { TraceabilityScreen } from '../features/traceability/TraceabilityScreen';
import { CampaignsScreen } from '../features/campaigns/CampaignsScreen';
import { CampaignPage } from '../features/campaigns/CampaignPage';
import { LocalePage } from '../features/money/LocalePage';
import { ContentScreen } from '../features/content/ContentScreen';
import { RegistrationsScreen } from '../features/money/RegistrationsScreen';
import { MoneyInScreen } from '../features/money/MoneyInScreen';
import { LocaleHealthScreen } from '../features/money/LocaleHealthScreen';
import { AlertsScreen } from '../features/money/AlertsScreen';
import { NexusScreen } from '../features/money/NexusScreen';
import { HomeRuleScreen } from '../features/money/HomeRuleScreen';
import { PayoutsScreen } from '../features/money/PayoutsScreen';
import { ReconciliationScreen } from '../features/money/ReconciliationScreen';
import { FilingScreen } from '../features/money/FilingScreen';
import { ReportsScreen } from '../features/money/ReportsScreen';
import { ForbiddenPage, NotFoundPage, RootNotFoundPage } from './pages/StatusPages';
import { NoNetworkPage } from './pages/NoNetworkPage';
import { UpdateRequiredPage } from './pages/UpdateRequiredPage';
import { ServiceUnavailableScreen } from './pages/ServiceUnavailablePage';
import { AccountPage } from '../features/account/AccountPage';
import { parseTableSearch } from '../lib/table/tableSearch';
import { GlobalErrorModal } from '../components/ui/GlobalErrorModal';
import { ExpiredLinkModal } from '../components/ui/ExpiredLinkModal';
import { PushBlockedModal } from '../components/ui/PushBlockedModal';
import { PushPermissionModal } from '../components/auth/PushPermissionModal';
import { PushFirstLaunchTrigger } from '../components/auth/PushFirstLaunchTrigger';
import { PushTapNavigator } from '../components/auth/PushTapNavigator';
import { SecretLoginHandler } from '../components/auth/SecretLoginHandler';
import { PwaUpdateInitializer } from '../components/auth/PwaUpdateInitializer';
import { VersionCheckRunner } from '../components/auth/VersionCheckRunner';
import { UpdateAvailableBanner } from '../components/ui/UpdateAvailableBanner';
import { AppToaster } from '../components/feedback/AppToaster';
import { SnagScreen } from '../components/ui/SnagScreen';

/**
 * Route tree (FE plan §3.6). Code-based TanStack Router:
 *
 *   __root                     platform runners + global modals + toaster
 *   ├─ /sign-in                public; bounces to `redirect` when signed in
 *   ├─ /no-network             public recovery screens
 *   ├─ /service-unavailable
 *   ├─ /update-required
 *   └─ _app  (AdminShell)      signed-in only — `?s=` secret links pass through
 *      ├─ /                    → the first screen the tier may open
 *      ├─ one route per nav item, each capability-checked (403 in place)
 *      └─ /account
 *
 * Every nav screen is a placeholder until its phase; F3+ replace each with
 * its feature's route, lazy-loaded, with validated search params.
 */

// --- root -----------------------------------------------------------------

function NetworkNavigators() {
  const navigate = useNavigate();
  useEffect(() => {
    const goNoNetwork = () => {
      if (window.location.pathname !== '/no-network') void navigate({ to: '/no-network' });
    };
    setNoNetworkNavigator(goNoNetwork);
    setServiceUnavailableNavigator(() => {
      if (window.location.pathname !== '/service-unavailable') void navigate({ to: '/service-unavailable' });
    });
    if (!navigator.onLine) goNoNetwork();
    window.addEventListener('offline', goNoNetwork);
    return () => {
      setNoNetworkNavigator(null);
      setServiceUnavailableNavigator(null);
      window.removeEventListener('offline', goNoNetwork);
    };
  }, [navigate]);
  return null;
}

const MOCK_API = import.meta.env.DEV && import.meta.env.VITE_MOCK_API === 'true';

/** Dev mock mode serves auth too — make it impossible to miss (plan D10). */
function MockApiBadge() {
  if (!MOCK_API) return null;
  return (
    <div
      role="status"
      className="pointer-events-none fixed bottom-3 right-3 z-[3000] rounded-pill bg-danger px-3 py-1 font-body text-xs font-bold uppercase tracking-[.14em] text-white shadow-lg"
    >
      Mock API — not your backend
    </div>
  );
}

function RootLayout() {
  return (
    <>
      <MockApiBadge />
      <NetworkNavigators />
      <SecretLoginHandler />
      <PushTapNavigator />
      <PwaUpdateInitializer />
      <VersionCheckRunner />
      <Outlet />
      <UpdateAvailableBanner />
      <GlobalErrorModal />
      <ExpiredLinkModal />
      <PushBlockedModal />
      <PushPermissionModal />
      <PushFirstLaunchTrigger />
      <AppToaster />
    </>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: RootNotFoundPage,
  errorComponent: SnagScreen,
});

// --- public ---------------------------------------------------------------

/** Only same-origin paths are honoured as a post-sign-in destination. */
function safeRedirect(target: unknown): string {
  return typeof target === 'string' && target.startsWith('/') && !target.startsWith('//') ? target : '/';
}

function SignInRoute() {
  const router = useRouter();
  const { redirect: target } = signInRoute.useSearch();
  return <SignInPage onSignedIn={() => router.history.push(safeRedirect(target))} />;
}

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (useAuthStore.getState().isAuthenticated) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: SignInRoute,
});

const noNetworkRoute = createRoute({ getParentRoute: () => rootRoute, path: '/no-network', component: NoNetworkPage });
const serviceUnavailableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/service-unavailable',
  component: ServiceUnavailableScreen,
});
const updateRequiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/update-required',
  component: UpdateRequiredPage,
});

// --- signed in ------------------------------------------------------------

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    if (useAuthStore.getState().isAuthenticated) return;
    // A `?s=<secret>` link is mid-handshake: SecretLoginHandler signs it in
    // on this URL, so don't bounce it to /sign-in and lose the secret.
    if (new URLSearchParams(location.searchStr).has('s')) return;
    throw redirect({ to: '/sign-in', search: { redirect: location.href } });
  },
  component: AdminShell,
  notFoundComponent: NotFoundPage,
});

/** Renders children only for a tier holding the capability; a 403 in place otherwise. */
/** Renders the screen when any of the read permissions is held; a 403 page otherwise. */
export function RequireCapability({
  capability,
  children,
}: {
  capability: Capability | readonly Capability[];
  children: ReactNode;
}) {
  const { can } = useSession();
  const caps = typeof capability === 'string' ? [capability] : capability;
  return caps.some(can) ? <>{children}</> : <ForbiddenPage />;
}

function HomeRedirect() {
  const { can } = useSession();
  return <Navigate to={homePathFor(can)} replace />;
}

const indexRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomeRedirect });

const accountRoute = createRoute({ getParentRoute: () => appRoute, path: '/account', component: AccountPage });

/** Campaign detail: a page under QR & Campaigns (same nav item, same capability). */
const campaignRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns/$campaignId',
  component: () => (
    <RequireCapability capability="core.read">
      <CampaignPage />
    </RequireCapability>
  ),
});

/** Locale detail: a page under Locale health (same nav item, same capability). */
const localeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/money/locale-health/$localeId',
  validateSearch: (search: Record<string, unknown>) => parseTableSearch(search),
  component: () => (
    <RequireCapability capability="money.health.read">
      <LocalePage />
    </RequireCapability>
  ),
});

/**
 * Dev-only component gallery (FE plan F2). Built inside the DEV check with a
 * lazy import, so a deployed build drops the route AND the page code.
 */
const devRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => appRoute,
        path: '/_gallery',
        validateSearch: (search: Record<string, unknown>) => parseTableSearch(search),
        component: lazyRouteComponent(() => import('./pages/GalleryPage'), 'GalleryPage'),
      }),
    ]
  : [];

/** Built screens by nav id. Their filters, sort, page and open dialog live in the URL. */
const SCREENS: Record<string, ComponentType> = {
  dashboard: DashboardScreen,
  locales: CommunityActivationScreen,
  analytics: GrowthScreen,
  chefs: ChefsScreen,
  eaters: EatersScreen,
  feedback: FeedbackScreen,
  traceability: TraceabilityScreen,
  campaigns: CampaignsScreen,
  content: ContentScreen,
  registrations: RegistrationsScreen,
  'money-in': MoneyInScreen,
  'locale-health': LocaleHealthScreen,
  alerts: AlertsScreen,
  nexus: NexusScreen,
  'home-rule': HomeRuleScreen,
  payouts: PayoutsScreen,
  reconciliation: ReconciliationScreen,
  filing: FilingScreen,
  reports: ReportsScreen,
};

function navRoute(item: NavItem) {
  const Screen = SCREENS[item.id];
  return createRoute({
    getParentRoute: () => appRoute,
    path: item.path,
    validateSearch: (search: Record<string, unknown>) => parseTableSearch(search),
    component: () => (
      <RequireCapability capability={item.capability}>{Screen ? <Screen /> : <PlaceholderScreen />}</RequireCapability>
    ),
  });
}

const routeTree = rootRoute.addChildren([
  signInRoute,
  noNetworkRoute,
  serviceUnavailableRoute,
  updateRequiredRoute,
  appRoute.addChildren([
    indexRoute,
    accountRoute,
    campaignRoute,
    localeRoute,
    ...devRoutes,
    ...NAV_ITEMS.map(navRoute),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Off: the window never scrolls here — the shell's content well does, and the
  // router's restoration copies that well's position from page to page (after
  // render), landing a new page mid-way down. AdminShell resets it per path.
  scrollRestoration: false,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
